import React, { useEffect, useState, useRef } from 'react';
import { Box, TextInput } from 'grommet';
import TagItem from './tag-item';
import { authorizedFetch, getSourceInfo } from '../utils';

const getSourceIds = (
  setSuggestions,
  exclude,
  filter,
  addError,
  sourceIdAbortController,
  trafficPartnerId
) => {
  if (sourceIdAbortController) {
    sourceIdAbortController.abort();
  }
  sourceIdAbortController = new AbortController();

  const query = {
    query: {
      bool: {
        must: [
          {
            exists: {
              field: 'properties.url.queryParams.sourceid'
            }
          }
        ]
      }
    },
    aggs: {
      matchingSearch: {
        terms: {
          field: 'properties.url.queryParams.sourceid',
          size: 10
        }
      }
    },
    size: 0
  };
  if (trafficPartnerId) {
    query.query.bool.filter = {
      term: {
        'trafficPartnerId.keyword': trafficPartnerId
      }
    };
  }
  if (filter) {
    //filter by text value
    query.query.bool.must.push({
      query_string: {
        default_field: 'properties.url.queryParams.sourceid',
        query: `*${filter}*`
      }
    });
  }

  authorizedFetch(
    'api/cds-search/reporting-event-*/_search?ignore_unavailable=true',
    'POST',
    query,
    sourceIdAbortController.signal
  )
    .then(cdsResults => {
      const qry = {
        query: {
          bool: {
            must: [],
            should: []
          }
        },
        _source: ['name', 'affiliateId'],
        size: 10
      };

      if (trafficPartnerId) {
        qry.query = {
          bool: {
            filter: {
              term: { 'trafficPartnerId.keyword': trafficPartnerId }
            }
          }
        };
      }

      const buckets = cdsResults?.aggregations?.matchingSearch?.buckets ?? [];

      if (filter) {
        //filter by text value
        qry.query.bool.should = [
          {
            terms: {
              affiliateId: buckets.map(b => b.key)
            }
          }
        ];
        qry.query.bool.must = [
          {
            query_string: {
              fields: ['name', 'affiliateId'],
              query: `*${filter}*`
            }
          }
        ];
      } else {
        //get matching names
        if (buckets?.length > 0) {
          qry.query = {
            terms: {
              affiliateId: buckets.map(b => b.key)
            }
          };
          qry.size = 50;
        }
      }

      authorizedFetch(
        'api/search/eventflow-trafficsourcereadmodel/_search',
        'POST',
        qry,
        sourceIdAbortController.signal
      )
        .then(eventResults => {
          const values = buckets.reduce((p, c) => {
            p[c.key] = c.key;
            return p;
          }, {});

          eventResults?.hits?.hits?.reduce((p, c) => {
            p[c._source.affiliateId.trim()] = c._source.name;
            return p;
          }, values);

          const responseBucket = [];
          for (const [key, value] of Object.entries(values)) {
            responseBucket.push({
              key: key,
              name: value,
              display: key !== value ? `${value} | ${key}` : key
            });
          }
          const suggestions = [];
          responseBucket.forEach(r => {
            if (!exclude.includes(r.key)) {
              suggestions.push(r);
            }
          });
          setSuggestions(suggestions);
        })
        .catch(ex => {
          if (ex.name !== 'AbortError') {
            addError('Unable to get event terms', JSON.stringify(ex));
          }
        });
    })
    .catch(ex => {
      if (ex.name !== 'AbortError') {
        addError('Unable to get event terms', JSON.stringify(ex));
      }
    });
};

const SourceidTagSelector = ({
  itemIds = [],
  excludeSourceIds = [],
  onItemAdd,
  onItemRemove,
  width = 'auto',
  disabled = false,
  size = 'medium',
  addError,
  placeholder = 'Add Value',
  trafficPartnerId = null
}) => {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [sourceInfo, setSourceInfo] = useState({});
  const textRef = useRef();

  let sourceIdAbortController = null;

  const itemIdsKey = itemIds.join(',');
  const excludeKey = excludeSourceIds.join(',');

  useEffect(() => {
    const getAndSetSourceInfo = async () => {
      const sourceInfoData = await getSourceInfo(trafficPartnerId);
      setSourceInfo(sourceInfoData);
    };

    getAndSetSourceInfo();
    getSourceIds(
      setSuggestions,
      [...itemIds, ...excludeSourceIds],
      '',
      addError,
      sourceIdAbortController,
      trafficPartnerId
    );
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      getSourceIds(
        setSuggestions,
        [...itemIds, ...excludeSourceIds],
        value,
        addError,
        sourceIdAbortController,
        trafficPartnerId
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [value, itemIdsKey, excludeKey]);

  const displayTag = id => {
    const name = sourceInfo[id]?.name;
    const displayValue = name ? `${name} | ${id}` : id;
    return displayValue;
  };

  return (
    <Box
      direction='row'
      border={{ color: 'border500' }}
      round='xsmall'
      style={{ width }}
    >
      <Box
        direction='row'
        wrap
        style={{ maxWidth: '65%' }}
        align='center'
        flex={{ shrink: 0 }}
      >
        {itemIds.map(id => (
          <TagItem
            maxWidth='250px'
            size={size}
            key={id}
            tag={displayTag(id)}
            disabled={disabled}
            onRemoveClick={() => onItemRemove(id)}
            tagTitleBuilder={t => t}
          />
        ))}
      </Box>
      <Box flex>
        <TextInput
          size={size}
          placeholder={placeholder}
          plain
          disabled={disabled}
          ref={textRef}
          value={value}
          onChange={e => {
            setSuggestions([]);
            setValue(e.target.value);
          }}
          suggestions={suggestions.map(m => m.display)}
          onSelect={e => {
            const result = suggestions.find(s => s.display === e.suggestion);
            if (!itemIds.includes(result.key)) {
              onItemAdd(result.key);
              setValue('');
            } else {
              getSourceIds(
                setSuggestions,
                [...itemIds, ...excludeSourceIds],
                value,
                addError,
                sourceIdAbortController,
                trafficPartnerId
              );
            }
          }}
        />
      </Box>
    </Box>
  );
};

SourceidTagSelector.displayName = 'SourceidTagSelector';

export default SourceidTagSelector;
